import React from 'react'
import { useFirebaseQuery } from '../hooks/firebaseHooks'
import { useOrganization } from '../context/OrganizationContext'
import { formatCurrency } from '../utils/shared/priceCalculator'
import { COLLECTIONS, PATHS } from '../utils/shared/constants'
import { DataTable } from '../components/DataTable'
import { useText } from '../context/TextContext'
import { Link } from 'react-router-dom'
import PageHeader from '../components/PageHeader'
import { Dropdown } from 'react-bootstrap'
import { ThreeDotsMenu } from '../components/ThreeDotsMenu'
import { AdvancedDropdownItem } from '../components/AdvancedDropdownItem'
import { useSaveToast } from '../context/SaveToastContext'
import { useFirestore } from 'reactfire'
import { doc, setDoc, where } from 'firebase/firestore'
import { EquipmentItem, EquipmentCategory } from '../models/shared/EquipmentItem'

export default function EquipmentPage() {
    const text = useText()
    const { orgId, organization } = useOrganization()
    const currency = organization.settings?.currency ?? 'SEK'
    const [items] = useFirebaseQuery<EquipmentItem>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.EQUIPMENT}`,
        where('status', '==', 'active')
    )
    const { startSaving, markSaved } = useSaveToast()
    const firestore = useFirestore()

    const handleDelete = async (itemId: string) => {
        const saveId = startSaving()
        const update: Partial<EquipmentItem> = { status: 'archived' }
        await setDoc(
            doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.EQUIPMENT}/${itemId}`),
            update,
            { merge: true }
        )
        markSaved(saveId)
    }


    const categoryLabel = (category: EquipmentCategory): string => {
        const map: Record<EquipmentCategory, string> = {
            chainsaw: text.equipmentCategoryChainsaw,
            climbing_gear: text.equipmentCategoryClimbingGear,
            truck: text.equipmentCategoryTruck,
            chipper: text.equipmentCategoryChipper,
            rigging: text.equipmentCategoryRigging,
            hand_tools: text.equipmentCategoryHandTools,
            other: text.equipmentCategoryOther,
        }
        return map[category] ?? category
    }

    const totalPurchase = items?.reduce((sum, i) => sum + (i.purchasePrice ?? 0), 0) ?? 0
    const totalCurrent = items?.reduce((sum, i) => sum + (i.currentValue ?? 0), 0) ?? 0

    return (
        <div className='d-flex flex-column'>
            <PageHeader
                title={text.equipment}
                description={text.equipmentDescription}
                actions={[{ text: text.newEquipment, icon: 'bi-plus', link: PATHS.EQUIPMENT_EDIT }]}
            />
            <div className='d-flex flex-column gap-4'>

                {(totalPurchase > 0 || totalCurrent > 0) && (
                    <div className='d-flex gap-4'>
                        <div className='card p-3'>
                            <div className='text-muted small'>{text.equipmentTotalPurchaseValue}</div>
                            <div className='fw-semibold'>{formatCurrency(totalPurchase, currency)}</div>
                        </div>
                        <div className='card p-3'>
                            <div className='text-muted small'>{text.equipmentTotalCurrentValue}</div>
                            <div className='fw-semibold'>{formatCurrency(totalCurrent, currency)}</div>
                        </div>
                    </div>
                )}

                <DataTable
                    data={items}
                    columns={[
                        {
                            label: text.name,
                            render: (item) => (
                                <Link to={`${PATHS.EQUIPMENT_EDIT}/${item.id}`}>{item.name}</Link>
                            ),
                        },
                        {
                            label: text.equipmentCategory,
                            render: (item) => categoryLabel(item.category),
                        },
                        {
                            label: text.equipmentMake,
                            render: (item) => <>{item.make ?? '—'}{item.model ? ` ${item.model}` : ''}</>,
                        },
                        {
                            label: text.equipmentSerialNumber,
                            render: (item) => <>{item.serialNumber ?? '—'}</>,
                        },
                        {
                            label: text.equipmentPurchasePrice,
                            align: 'right',
                            render: (item) => <>{item.purchasePrice != null ? formatCurrency(item.purchasePrice, currency) : '—'}</>,
                        },
                        {
                            label: text.equipmentCurrentValue,
                            align: 'right',
                            render: (item) => <>{item.currentValue != null ? formatCurrency(item.currentValue, currency) : '—'}</>,
                        },
                        {
                            label: text.actions,
                            align: 'right',
                            render: (item) => (
                                <ThreeDotsMenu>
                                    <AdvancedDropdownItem
                                        icon='bi-pencil'
                                        text={text.edit}
                                        link={`${PATHS.EQUIPMENT_EDIT}/${item.id}`}
                                    />
                                    <Dropdown.Divider />
                                    <AdvancedDropdownItem
                                        onClick={() => handleDelete(item.id ?? '')}
                                        icon='bi-trash'
                                        text={text.archive}
                                    />
                                </ThreeDotsMenu>
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    )
}
